import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Money } from '@rental/shared';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { Badge, Button, Card, Empty, Field, Page, TextInput } from '../components/ui.jsx';

/**
 * Friendly, action-first dashboard.
 *
 * Aims to answer three questions before you have to click anywhere:
 *   1. How is this month going? (net profit tile)
 *   2. What needs my attention? (pending rent, due bills)
 *   3. How do I add today's activity fast? (quick-add row)
 */

interface Breakdown {
  year: number; month: number;
  rentalIncome: number; additionalIncome: number; totalIncome: number;
  propertyExpenses: number; electricityExpenses: number;
  installationExpenses: number; otherExpenses: number;
  totalExpenses: number; netProfit: number;
  rent: { expected: number; received: number; pending: number; overpaid: number };
  perProperty: { propertyId: string; name: string; rentalIncome: number; expenses: number; netProfit: number }[];
  closed: boolean;
}
interface Invoice {
  id: string; leaseId: string; unitId: string; tenantId: string;
  amountExpected: number; amountReceived: number;
  status: 'pending' | 'partial' | 'paid' | 'overpaid' | 'waived';
}
interface Tenant { id: string; fullName: string; }
interface Bill {
  id: string; connectionId: string; totalAmount: number;
  dueDate: string | null; paymentStatus: 'paid' | 'unpaid' | 'unknown';
}
interface Property { id: string; name: string; }
interface Lease { id: string; }
interface Category { slug: string; name: string; kind: 'opex' | 'installation' | 'other'; }

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export function DashboardPage() {
  const { user } = useAuth();
  const now = new Date();
  const y = now.getUTCFullYear(), m = now.getUTCMonth() + 1;

  const dash = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api<{ breakdown: Breakdown }>('/api/v1/reports/dashboard'),
    refetchInterval: 60_000,
  });
  const invoices = useQuery({
    queryKey: ['rent', y, m, 'unpaid'],
    queryFn: () => api<{ invoices: Invoice[] }>(`/api/v1/rent/invoices?year=${y}&month=${m}`),
  });
  const tenants = useQuery({
    queryKey: ['tenants'],
    queryFn: () => api<{ tenants: Tenant[] }>('/api/v1/tenants'),
  });
  const bills = useQuery({
    queryKey: ['electricity-bills'],
    queryFn: () => api<{ bills: Bill[] }>('/api/v1/electricity/bills'),
  });
  const properties = useQuery({
    queryKey: ['properties'],
    queryFn: () => api<{ properties: Property[] }>('/api/v1/properties'),
  });
  const leases = useQuery({
    queryKey: ['leases-active'],
    queryFn: () => api<{ leases: Lease[] }>('/api/v1/leases?status=active'),
  });

  const tenantName = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of tenants.data?.tenants ?? []) map.set(t.id, t.fullName);
    return map;
  }, [tenants.data]);

  const b = dash.data?.breakdown;
  const firstName = user?.email?.split('@')[0]?.split(/[._-]/)[0] ?? 'there';
  const greeting = friendlyGreeting();

  const unpaid = (invoices.data?.invoices ?? []).filter(
    (i) => i.status === 'pending' || i.status === 'partial',
  );
  const unpaidBills = (bills.data?.bills ?? []).filter((b) => b.paymentStatus !== 'paid').slice(0, 3);

  const noProperties = properties.data?.properties.length === 0;
  const noActiveLeases = (leases.data?.leases ?? []).length === 0;
  const notReadyYet = properties.isSuccess && (noProperties || noActiveLeases);

  return (
    <Page
      title={`${greeting}, ${capitalize(firstName)}`}
      subtitle={`Here's ${MONTHS[m - 1]} ${y} at a glance.`}
    >
      {notReadyYet && (
        <Card tone="accent" className="mb-6">
          <div className="flex items-start gap-4">
            <div className="text-3xl">👋</div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold">
                {noProperties ? 'Set up your first rental' : 'Add a tenant to start tracking rent'}
              </h2>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                {noProperties
                  ? 'Add your property, room and tenant in one screen. Takes a minute.'
                  : 'Your property is set up. Now start a lease so rent invoices get generated.'}
              </p>
            </div>
            <Link to="/setup">
              <Button>Start setup</Button>
            </Link>
          </div>
        </Card>
      )}

      {/* Big net-profit hero card */}
      <div className="mb-8 grid gap-3 md:grid-cols-3">
        <Card className="md:col-span-2" tone={b && b.netProfit >= 0 ? 'good' : 'bad'}>
          <div className="text-sm text-slate-500">Net profit this month</div>
          <div className={`mt-1 text-4xl font-semibold ${b && b.netProfit >= 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}`}>
            {b ? Money.fromPaise(b.netProfit).format() : '—'}
          </div>
          {b && (
            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-500">
              <span>Income {Money.fromPaise(b.totalIncome).format()}</span>
              <span>Expenses {Money.fromPaise(b.totalExpenses).format()}</span>
              {b.closed && <Badge tone="good">Month closed</Badge>}
            </div>
          )}
        </Card>
        <Card tone="accent">
          <div className="text-sm text-slate-500">Rent received</div>
          <div className="mt-1 text-2xl font-semibold">
            {b ? Money.fromPaise(b.rent.received).format() : '—'}
          </div>
          <div className="mt-2 text-xs text-slate-500">
            Expected {b ? Money.fromPaise(b.rent.expected).format() : '—'}
            {b && b.rent.pending > 0 && (
              <span className="ml-2 text-amber-700 dark:text-amber-300">
                · Pending {Money.fromPaise(b.rent.pending).format()}
              </span>
            )}
          </div>
        </Card>
      </div>

      {/* Quick actions */}
      <div className="mb-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">Quick add</h2>
        <QuickAdd onDone={() => dash.refetch()} />
      </div>

      {/* Needs attention */}
      <div className="mb-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">Needs your attention</h2>
        {invoices.isLoading ? (
          <Empty>Loading…</Empty>
        ) : unpaid.length === 0 && unpaidBills.length === 0 ? (
          <Card><p className="text-sm text-slate-500">Nothing outstanding right now. 🎉</p></Card>
        ) : (
          <div className="space-y-2">
            {unpaid.map((inv) => (
              <PendingRentRow
                key={inv.id}
                invoice={inv}
                tenantName={tenantName.get(inv.tenantId) ?? '—'}
                onPaid={() => { void invoices.refetch(); void dash.refetch(); }}
              />
            ))}
            {unpaidBills.map((b) => (
              <Card key={b.id} className="!p-3">
                <div className="flex items-center justify-between text-sm">
                  <div>
                    <div className="font-medium">Electricity bill</div>
                    <div className="text-xs text-slate-500">
                      {b.dueDate ? `Due ${b.dueDate}` : 'No due date'}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium">{Money.fromPaise(b.totalAmount).format()}</div>
                    <Link to="/electricity" className="text-xs underline decoration-dotted">Open</Link>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Per-property */}
      {b && b.perProperty.length > 0 && (
        <div>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">By property</h2>
          <Card>
            <ul className="divide-y divide-slate-200 dark:divide-slate-700">
              {b.perProperty.map((p) => (
                <li key={p.propertyId} className="flex items-center justify-between py-3 first:pt-0 last:pb-0">
                  <div>
                    <Link to={`/properties/${p.propertyId}`} className="font-medium underline decoration-dotted">
                      {p.name}
                    </Link>
                    <div className="text-xs text-slate-500">
                      In {Money.fromPaise(p.rentalIncome).format()} · Out {Money.fromPaise(p.expenses).format()}
                    </div>
                  </div>
                  <div className={`text-lg font-semibold ${p.netProfit >= 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}`}>
                    {Money.fromPaise(p.netProfit).format()}
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      )}
    </Page>
  );
}

function PendingRentRow({ invoice, tenantName, onPaid }: { invoice: Invoice; tenantName: string; onPaid: () => void }) {
  const [open, setOpen] = useState(false);
  const pending = Math.max(0, invoice.amountExpected - invoice.amountReceived);
  return (
    <Card className="!p-3">
      <div className="flex items-center justify-between gap-3 text-sm">
        <div className="min-w-0">
          <div className="truncate font-medium">{tenantName}</div>
          <div className="text-xs text-slate-500">
            Rent pending {Money.fromPaise(pending).format()}
            {invoice.amountReceived > 0 && ` · Received ${Money.fromPaise(invoice.amountReceived).format()}`}
          </div>
        </div>
        <Button size="sm" onClick={() => setOpen((v) => !v)}>
          {open ? 'Cancel' : 'Record'}
        </Button>
      </div>
      {open && (
        <div className="mt-3 border-t border-slate-200 pt-3 dark:border-slate-700">
          <RecordPaymentForm invoiceId={invoice.id} suggested={pending} onDone={() => { setOpen(false); onPaid(); }} />
        </div>
      )}
    </Card>
  );
}

function RecordPaymentForm({ invoiceId, suggested, onDone }: {
  invoiceId: string; suggested: number; onDone: () => void;
}) {
  const [amount, setAmount] = useState(suggested > 0 ? Money.fromPaise(suggested).toRupees().toString() : '');
  const [method, setMethod] = useState<'cash' | 'bank' | 'upi' | 'cheque' | 'other'>('upi');
  const [error, setError] = useState<string | null>(null);
  const record = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api(`/api/v1/rent/invoices/${invoiceId}/payments`, { method: 'POST', body }),
    onSuccess: onDone,
    onError: (e) => setError(e instanceof Error ? e.message : 'Save failed'),
  });
  function submit(e: FormEvent) {
    e.preventDefault(); setError(null);
    try {
      record.mutate({
        amount: Money.parse(amount).toPaise(),
        paidOn: new Date().toISOString().slice(0, 10),
        method,
      });
    } catch (err) { setError(err instanceof Error ? err.message : 'Invalid amount'); }
  }
  return (
    <form onSubmit={submit} className="grid gap-3 md:grid-cols-4 md:items-end">
      <Field label="Amount"><TextInput required value={amount} onChange={(e) => setAmount(e.target.value)} /></Field>
      <Field label="Method">
        <select value={method} onChange={(e) => setMethod(e.target.value as typeof method)}
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-800">
          <option value="cash">Cash</option><option value="bank">Bank</option>
          <option value="upi">UPI</option><option value="cheque">Cheque</option>
          <option value="other">Other</option>
        </select>
      </Field>
      <div className="md:col-span-2 flex items-center justify-end gap-2">
        {error && <span className="text-xs text-red-600">{error}</span>}
        <Button type="submit" size="sm" disabled={record.isPending}>
          {record.isPending ? 'Saving…' : 'Save payment'}
        </Button>
      </div>
    </form>
  );
}

function QuickAdd({ onDone }: { onDone: () => void }) {
  const [open, setOpen] = useState<'expense' | 'income' | null>(null);
  return (
    <div className="grid gap-3 md:grid-cols-3">
      <ActionTile
        title="Add expense"
        subtitle="Repair, maintenance, bills"
        icon="💸"
        onClick={() => setOpen((v) => v === 'expense' ? null : 'expense')}
        active={open === 'expense'}
      />
      <ActionTile
        title="Add income"
        subtitle="Salary, freelance, other"
        icon="💰"
        onClick={() => setOpen((v) => v === 'income' ? null : 'income')}
        active={open === 'income'}
      />
      <ActionTile
        title="Add new rental"
        subtitle="Property, room, tenant, rent"
        icon="🏠"
        href="/setup"
      />
      {open === 'expense' && (
        <div className="md:col-span-3">
          <QuickExpenseForm onDone={() => { setOpen(null); onDone(); }} />
        </div>
      )}
      {open === 'income' && (
        <div className="md:col-span-3">
          <QuickIncomeForm onDone={() => { setOpen(null); onDone(); }} />
        </div>
      )}
    </div>
  );
}

function ActionTile({ title, subtitle, icon, onClick, href, active }: {
  title: string; subtitle: string; icon: string;
  onClick?: () => void; href?: string; active?: boolean;
}) {
  const inner = (
    <div className={`rounded-xl border p-4 transition ${active ? 'border-slate-900 bg-slate-50 dark:border-slate-100 dark:bg-slate-800' : 'border-slate-200 bg-white hover:border-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-slate-500'}`}>
      <div className="text-2xl">{icon}</div>
      <div className="mt-2 font-medium">{title}</div>
      <div className="text-xs text-slate-500">{subtitle}</div>
    </div>
  );
  if (href) return <Link to={href} className="block cursor-pointer text-left">{inner}</Link>;
  return <button type="button" onClick={onClick} className="block w-full cursor-pointer text-left">{inner}</button>;
}

function QuickExpenseForm({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const cats = useQuery({
    queryKey: ['expense-categories'],
    queryFn: () => api<{ categories: Category[] }>('/api/v1/expenses/categories'),
  });
  const props = useQuery({
    queryKey: ['properties'],
    queryFn: () => api<{ properties: Property[] }>('/api/v1/properties'),
  });
  const [amount, setAmount] = useState('');
  const [desc, setDesc] = useState('');
  const [slug, setSlug] = useState('repair');
  const [propertyId, setPropertyId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api('/api/v1/expenses', { method: 'POST', body }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['expenses'] }); onDone(); },
    onError: (e) => setError(e instanceof Error ? e.message : 'Save failed'),
  });
  function submit(e: FormEvent) {
    e.preventDefault(); setError(null);
    try {
      save.mutate({
        amount: Money.parse(amount).toPaise(),
        spentOn: new Date().toISOString().slice(0, 10),
        categorySlug: slug,
        description: desc || undefined,
        propertyId: propertyId || undefined,
      });
    } catch (err) { setError(err instanceof Error ? err.message : 'Invalid amount'); }
  }
  return (
    <Card>
      <form onSubmit={submit} className="grid gap-3 md:grid-cols-5 md:items-end">
        <Field label="Amount (₹)"><TextInput required value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="2500" /></Field>
        <Field label="Category">
          <select value={slug} onChange={(e) => setSlug(e.target.value)}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-800">
            {(cats.data?.categories ?? []).map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="Property">
          <select value={propertyId} onChange={(e) => setPropertyId(e.target.value)}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-800">
            <option value="">—</option>
            {(props.data?.properties ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
        <div className="md:col-span-2">
          <Field label="What for?">
            <TextInput value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Bathroom pipe replacement" />
          </Field>
        </div>
        <div className="md:col-span-5 flex items-center justify-end gap-2">
          {error && <span className="text-xs text-red-600">{error}</span>}
          <Button type="button" variant="ghost" size="sm" onClick={onDone}>Cancel</Button>
          <Button type="submit" size="sm" disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Add expense'}</Button>
        </div>
      </form>
    </Card>
  );
}

function QuickIncomeForm({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const cats = useQuery({
    queryKey: ['income-categories'],
    queryFn: () => api<{ categories: Category[] }>('/api/v1/incomes/categories'),
  });
  const [amount, setAmount] = useState('');
  const [source, setSource] = useState('');
  const [slug, setSlug] = useState('salary');
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api('/api/v1/incomes', { method: 'POST', body }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['incomes'] }); onDone(); },
    onError: (e) => setError(e instanceof Error ? e.message : 'Save failed'),
  });
  function submit(e: FormEvent) {
    e.preventDefault(); setError(null);
    try {
      save.mutate({
        amount: Money.parse(amount).toPaise(),
        receivedOn: new Date().toISOString().slice(0, 10),
        categorySlug: slug,
        source: source || undefined,
      });
    } catch (err) { setError(err instanceof Error ? err.message : 'Invalid amount'); }
  }
  return (
    <Card>
      <form onSubmit={submit} className="grid gap-3 md:grid-cols-4 md:items-end">
        <Field label="Amount (₹)"><TextInput required value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="75000" /></Field>
        <Field label="Source"><TextInput value={source} onChange={(e) => setSource(e.target.value)} placeholder="Acme Corp" /></Field>
        <Field label="Category">
          <select value={slug} onChange={(e) => setSlug(e.target.value)}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-800">
            {(cats.data?.categories ?? []).map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
        </Field>
        <div className="flex items-center justify-end gap-2">
          {error && <span className="text-xs text-red-600">{error}</span>}
          <Button type="button" variant="ghost" size="sm" onClick={onDone}>Cancel</Button>
          <Button type="submit" size="sm" disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Add income'}</Button>
        </div>
      </form>
    </Card>
  );
}

function friendlyGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}
function capitalize(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}
